import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2, User, Mail, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatBot = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const customPrompt = searchParams.get('prompt') || '';
  const customApiKey = searchParams.get('api_key') || '';
  const position = searchParams.get('position') || 'left';
  const websiteId = searchParams.get('website_id') || 'directmedia';
  const embedMode = searchParams.get('embed') === '1';
  const compactMode = searchParams.get('compact') === '1';
  const positionClass = position === 'right' ? 'bottom-6 right-6' : 'bottom-6 left-6';

  const storageKey = useCallback((key) => `chat_${websiteId}_${key}`, [websiteId]);

  const [botColor, setBotColor] = useState('#2563eb');
  const [botName, setBotName] = useState('Direct Media');
  const [welcomeTitle, setWelcomeTitle] = useState('مرحباً بك!');
  const [welcomeSubtitle, setWelcomeSubtitle] = useState('من فضلك ادخل بياناتك للبدء');
  const [isValidWebsite, setIsValidWebsite] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [isOpen, setIsOpen] = useState(embedMode && !compactMode);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(storageKey('sessionId')) || null);
  const [showForm, setShowForm] = useState(() => !localStorage.getItem(storageKey('sessionId')));
  const [customerName, setCustomerName] = useState(() => localStorage.getItem(storageKey('customerName')) || '');
  const [customerEmail, setCustomerEmail] = useState(() => localStorage.getItem(storageKey('customerEmail')) || '');
  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem(storageKey('messages'));
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return [];
      }
    }
    return [];
  });
  
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    if (messages.length > 0) {
      localStorage.setItem(storageKey('messages'), JSON.stringify(messages));
    }
  }, [messages, storageKey]);

  useEffect(() => {
    window.parent.postMessage({ type: 'CHATBOT_RESIZE', isOpen }, '*');
    if (isOpen && !showForm) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, showForm]);

  useEffect(() => {
    axios.get(`${API}/widget/settings/${websiteId}`).then(res => {
      setIsValidWebsite(res.data.valid !== false);
      if (res.data.bot_color) setBotColor(res.data.bot_color);
      if (res.data.bot_name) setBotName(res.data.bot_name);
      if (res.data.welcome_title) setWelcomeTitle(res.data.welcome_title);
      if (res.data.welcome_subtitle) setWelcomeSubtitle(res.data.welcome_subtitle);
      setSettingsLoaded(true);
    }).catch(() => {
      setSettingsLoaded(true);
    });
  }, [websiteId]);

  useEffect(() => {
    if (!embedMode) return;
    document.documentElement.style.background = 'transparent';
    document.documentElement.style.height = '100%';
    document.documentElement.style.margin = '0';
    document.body.style.background = 'transparent';
    document.body.style.height = '100%';
    document.body.style.margin = '0';
    document.body.style.overflow = 'hidden';
    const root = document.getElementById('root');
    if (root) {
      root.style.background = 'transparent';
      root.style.height = '100%';
    }
    return () => {
      document.documentElement.style.background = '';
      document.documentElement.style.height = '';
      document.documentElement.style.margin = '';
      document.body.style.background = '';
      document.body.style.height = '';
      document.body.style.margin = '';
      document.body.style.overflow = '';
      if (root) {
        root.style.background = '';
        root.style.height = '';
      }
    };
  }, [embedMode]);

  useEffect(() => {
    if (!isLoading && !showForm && isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isLoading, messages.length, isOpen, showForm]);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleStartChat = async (e) => {
    e.preventDefault();
    
    const errors = {};
    if (!customerName.trim()) {
      errors.name = 'من فضلك ادخل اسمك';
    }
    if (!customerEmail.trim()) {
      errors.email = 'من فضلك ادخل بريدك الإلكتروني';
    } else if (!validateEmail(customerEmail)) {
      errors.email = 'البريد الإلكتروني غير صحيح';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/chat/start-session`, {
        customer_name: customerName,
        customer_email: customerEmail,
        website_id: websiteId
      });

      const newSessionId = response.data.session_id;
      setSessionId(newSessionId);
      setShowForm(false);
      
      localStorage.setItem(storageKey('sessionId'), newSessionId);
      localStorage.setItem(storageKey('customerName'), customerName);
      localStorage.setItem(storageKey('customerEmail'), customerEmail);
      
      const welcomeMsg = {
        text: `مرحباً ${customerName}! 👋\n\nأنا مساعدك الذكي من ${botName}.\n\nكيف يمكنني مساعدتك اليوم؟`,
        sender: 'bot',
        timestamp: new Date()
      };
      
      setMessages([welcomeMsg]);
    } catch (error) {
      console.error('خطأ في بدء المحادثة:', error);
      setFormErrors({ general: 'حدث خطأ. يرجى المحاولة مرة أخرى.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || isLoading || !sessionId) return;

    const userMessage = {
      text: input,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const payload = {
        session_id: sessionId,
        message: input
      };
      if (customPrompt) payload.system_prompt = customPrompt;
      if (customApiKey) payload.api_key = customApiKey;

      const response = await axios.post(`${API}/chat`, payload);

      const botMessage = {
        text: response.data.reply,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('خطأ في إرسال الرسالة:', error);
      const errorMessage = {
        text: 'عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.',
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCloseClick = () => {
    if (!showForm) {
      setShowCloseConfirm(true);
    } else {
      setIsOpen(false);
      if (isCompactEmbed) {
        window.parent.postMessage({ type: 'CHATBOT_CLOSE' }, '*');
      }
    }
  };

  const confirmEndSession = () => {
    localStorage.removeItem(storageKey('sessionId'));
    localStorage.removeItem(storageKey('customerName'));
    localStorage.removeItem(storageKey('customerEmail'));
    localStorage.removeItem(storageKey('messages'));
    
    setSessionId(null);
    setCustomerName('');
    setCustomerEmail('');
    setMessages([]);
    setShowForm(true);
    setShowCloseConfirm(false);
    setIsOpen(false);
    if (embedMode && compactMode) {
      window.parent.postMessage({ type: 'CHATBOT_CLOSE' }, '*');
    }
  };

  const confirmMinimize = () => {
    setShowCloseConfirm(false);
    setIsOpen(false);
    if (embedMode && compactMode) {
      window.parent.postMessage({ type: 'CHATBOT_CLOSE' }, '*');
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('ar-EG', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isCompactEmbed = embedMode && compactMode;

  if (!settingsLoaded || !isValidWebsite) return null;

  const colorWithOpacity = (hex, opacity) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${opacity})`;
  };
  const darkenColor = (hex, amount = 0.15) => {
    const r = Math.max(0, Math.round(parseInt(hex.slice(1, 3), 16) * (1 - amount)));
    const g = Math.max(0, Math.round(parseInt(hex.slice(3, 5), 16) * (1 - amount)));
    const b = Math.max(0, Math.round(parseInt(hex.slice(5, 7), 16) * (1 - amount)));
    return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
  };

  return (
    <div
      className={isCompactEmbed ? (isOpen ? 'fixed inset-0' : 'fixed inset-0 flex items-end justify-end p-3') : `fixed z-50 ${positionClass}`}
      data-testid="chatbot-container"
      style={
        isCompactEmbed
          ? { background: 'transparent' }
          : embedMode
            ? { background: 'transparent', pointerEvents: 'none' }
            : undefined
      }
    >
      {isOpen && (
        <div
          className={
            isCompactEmbed
              ? 'bg-white overflow-hidden flex flex-col absolute inset-0'
              : 'mb-4 bg-white rounded-2xl shadow-2xl overflow-hidden relative flex flex-col'
          }
          style={isCompactEmbed ? {} : { width: '420px', height: '660px', ...(embedMode ? { pointerEvents: 'auto' } : {}) }}
          data-testid="chat-window"
        >
          {showCloseConfirm && (
            <div className="absolute inset-0 bg-black/50 z-50 flex items-center justify-center p-4" dir="rtl">
              <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-2xl">
                <div className="flex items-center gap-3 mb-4 text-gray-900">
                  <div className="p-2 rounded-full" style={{ background: colorWithOpacity(botColor, 0.15) }}>
                    <AlertCircle className="w-6 h-6" style={{ color: botColor }} />
                  </div>
                  <h3 className="font-bold text-lg">إغلاق المحادثة؟</h3>
                </div>
                <p className="text-gray-600 mb-6 text-sm">
                  هل تريد تصغير نافذة المحادثة فقط أم إنهاء المحادثة الحالية بشكل كامل؟
                </p>
                <div className="space-y-3">
                  <button
                    onClick={confirmMinimize}
                    className="w-full text-white py-2.5 rounded-lg transition-colors font-medium"
                    style={{ background: botColor }}
                  >
                    تصغير النافذة (حفظ المحادثة)
                  </button>
                  <button
                    onClick={confirmEndSession}
                    className="w-full bg-red-50 text-red-600 py-2.5 rounded-lg hover:bg-red-100 transition-colors font-medium"
                  >
                    إنهاء المحادثة كاملة
                  </button>
                  <button
                    onClick={() => setShowCloseConfirm(false)}
                    className="w-full text-gray-500 py-2 hover:bg-gray-50 rounded-lg transition-colors text-sm"
                  >
                    إلغاء
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="text-white p-4 flex items-center justify-between gap-3" style={{ background: `linear-gradient(to right, ${botColor}, ${darkenColor(botColor)})` }}>
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-6 h-6" style={{ color: botColor }} />
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-base" data-testid="chat-title">{botName}</h3>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>مساعدك الذكي</p>
              </div>
            </div>
            <button
              onClick={handleCloseClick}
              className="flex-shrink-0 min-w-[40px] min-h-[40px] flex items-center justify-center rounded-full hover:bg-white/20 transition-colors"
              title="إغلاق"
              data-testid="close-chat-button"
            >
              <X className="w-6 h-6" strokeWidth={2.5} />
            </button>
          </div>

          {showForm && (
            <div className="flex-1 flex items-center justify-center p-6 bg-gray-50 overflow-y-auto" dir="rtl">
              <div className="w-full max-w-sm my-auto">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: colorWithOpacity(botColor, 0.15) }}>
                    <MessageCircle className="w-8 h-8" style={{ color: botColor }} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{welcomeTitle}</h3>
                  <p className="text-sm text-gray-600">{welcomeSubtitle}</p>
                </div>

                <form onSubmit={handleStartChat} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline ml-1" />
                      الاسم
                    </label>
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => {
                        setCustomerName(e.target.value);
                        setFormErrors({ ...formErrors, name: '' });
                      }}
                      placeholder="ادخل اسمك"
                      className={`w-full px-4 py-2.5 border ${formErrors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
                    />
                    {formErrors.name && (
                      <p className="text-xs text-red-500 mt-1">{formErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-4 h-4 inline ml-1" />
                      البريد الإلكتروني
                    </label>
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => {
                        setCustomerEmail(e.target.value);
                        setFormErrors({ ...formErrors, email: '' });
                      }}
                      placeholder="example@email.com"
                      className={`w-full px-4 py-2.5 border ${formErrors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500`}
                    />
                    {formErrors.email && (
                      <p className="text-xs text-red-500 mt-1">{formErrors.email}</p>
                    )}
                  </div>

                  {formErrors.general && (
                    <p className="text-sm text-red-500 text-center">{formErrors.general}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full text-white py-3 rounded-lg disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium"
                    style={isLoading ? {} : { background: botColor }}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        جاري البدء...
                      </span>
                    ) : (
                      'ابدأ المحادثة'
                    )}
                  </button>
                </form>
              </div>
            </div>
          )}

          {!showForm && (
            <>
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50" dir="rtl" data-testid="messages-container">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`mb-4 flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                        message.sender === 'user'
                          ? 'text-white rounded-br-sm'
                          : 'bg-white text-gray-800 rounded-bl-sm shadow-sm'
                      }`}
                      style={message.sender === 'user' ? { background: botColor } : {}}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
                      <p
                        className={`text-xs mt-1 ${
                          message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'
                        }`}
                      >
                        {formatTime(message.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex justify-start mb-4">
                    <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                      <Loader2 className="w-5 h-5 animate-spin" style={{ color: botColor }} />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-3 bg-white border-t border-gray-200" dir="rtl">
                <div className="flex items-end gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="اكتب رسالتك هنا..."
                    className="flex-1 resize-none border border-gray-300 rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    rows="1"
                    style={{ maxHeight: '80px', minHeight: '42px' }}
                    disabled={isLoading}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!input.trim() || isLoading}
                    className="text-white rounded-full p-2.5 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    style={(!input.trim() || isLoading) ? {} : { background: botColor }}
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {!isOpen && (!embedMode || compactMode) && (
        <button
          onClick={() => {
            if (embedMode && compactMode) {
              window.parent.postMessage({ type: 'CHATBOT_OPEN' }, '*');
            }
            setIsOpen(true);
          }}
          className="text-white rounded-full p-4 shadow-2xl hover:shadow-3xl hover:scale-110 transition-all duration-300 flex items-center gap-3 group"
          style={{ background: botColor }}
          data-testid="open-chat-button"
        >
          <MessageCircle className="w-7 h-7" />
          <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap font-medium" dir="rtl">
            تحدث معنا
          </span>
        </button>
      )}
    </div>
  );
};


export default ChatBot;